// The phone's conversation survives a page reload (the mic's last resort, iOS dropping the tab):
// the transcript is saved in this browser, keyed by the pairing, and shown again before the
// page even connects. Unpairing forgets it.
import type { TranscriptModel, TranscriptSnapshot } from "../transcript";

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** Most entries kept. */
export const HISTORY_MAX = 200;
/** Saved text kept under this many characters (oldest entries dropped first). */
const MAX_CHARS = 1_500_000;
const PREFIX = "lv-history:";

/** The pairing's device id (the middle of `<deskId>.<deviceId>.<secret>`): never the secret. */
export function historyKey(token: string): string {
  const [desk = "", device = ""] = token.split(".");
  return `${PREFIX}${desk}.${device}`;
}

export function loadHistory(key: string, storage: Store | null = safeStorage()): TranscriptSnapshot | null {
  try {
    const snap = JSON.parse(storage?.getItem(key) ?? "null");
    return snap && snap.v === 1 && Array.isArray(snap.entries) ? snap : null;
  } catch {
    return null;
  }
}

export function saveHistory(key: string, model: TranscriptModel, storage: Store | null = safeStorage()): void {
  if (!storage) return;
  let limit = HISTORY_MAX;
  for (;;) {
    const json = JSON.stringify(model.snapshot(limit));
    if (json.length <= MAX_CHARS || limit <= 10) {
      try {
        storage.setItem(key, json);
        return;
      } catch {
        if (limit <= 10) return; // storage full or blocked: the conversation just isn't kept
      }
    }
    limit = Math.floor(limit / 2);
  }
}

/** Forgets every saved conversation (unpair). */
export function clearHistory(storage: Store | null = safeStorage()): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    const s = storage as Storage;
    for (let i = 0; i < (s.length ?? 0); i++) {
      const k = s.key(i);
      if (k?.startsWith(PREFIX)) keys.push(k);
    }
    for (const k of keys) storage.removeItem(k);
  } catch {
    /* nothing kept */
  }
}

export interface History {
  /** Saves now (before a reload, when the page is hidden). */
  flush(): void;
  /** Stops saving and forgets this pairing's conversation. */
  clear(): void;
}

/**
 * Restores the pairing's saved conversation into `model`, then keeps it saved as it changes
 * (at most every `delayMs`, and at once when the page is hidden or unloads).
 */
export function keepHistory(model: TranscriptModel, key: string, storage: Store | null = safeStorage(), delayMs = 300): History {
  model.restore(loadHistory(key, storage));
  let timer: ReturnType<typeof setTimeout> | null = null;
  let on = true;
  const flush = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (on) saveHistory(key, model, storage);
  };
  const off = model.onChange(() => {
    if (on && !timer) timer = setTimeout(flush, delayMs);
  });
  const hidden = () => document.visibilityState === "hidden" && flush();
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", hidden);
  if (typeof window !== "undefined") window.addEventListener("pagehide", flush);
  return {
    flush,
    clear() {
      on = false;
      off();
      if (timer) clearTimeout(timer);
      timer = null;
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", hidden);
      if (typeof window !== "undefined") window.removeEventListener("pagehide", flush);
      try {
        storage?.removeItem(key);
      } catch {
        /* nothing kept */
      }
    },
  };
}

function safeStorage(): Store | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}
